import NavLinks from 'mixins/nav-links.js'
import NavigationLinks from 'components/StaticPage/navigation-links/index.vue'
import CitiesByCountry from 'components/StaticPage/CitiesByCountry/CitiesByCountry.vue'
import FaqBox from 'components/StaticPage/FaqBox/FaqBox.vue'
import BoxNotFound from 'components/BoxNotFound/BoxNotFound.vue'
import AppSearchV2 from 'components/Search/FormV2/index.vue'
import JsonLD from 'mixins/json-ld'
import Breadcrumb from 'components/Breadcrumb/Breadcrumb.vue'
import i18nConfig from 'config/i18n'
import Moment from 'moment-timezone'
import { PROMOTION_DATES } from 'config/promotion-dates'
import LocationDescription from 'components/LocationDescription/LocationDescription.vue'
import SeoService from 'services/SeoService'
import InsiderMixin from '@/mixins/insider-mixin'

export default {
  fetch({ store }) {
    store.dispatch('header/SET_CONFIG', {
      logo: true,
      menu: true,
      recentSearch: true,
      showFlag: true
    })

    store.dispatch('footer/SET_CONFIG', {
      show: true
    })
  },

  components: {
    BoxNotFound,
    NavigationLinks,
    CitiesByCountry,
    FaqBox,
    AppSearchV2,
    Breadcrumb,
    LocationDescription
  },

  name: 'view-locations-country',

  mixins: [JsonLD, NavLinks, InsiderMixin],

  data() {
    return {
      hotJarRoutes: {
        '/pt-br/localidades/uruguai': true,
        '/pt-br/localidades/portugal': true,
        '/es-co/locations/mexico': true
      }
    }
  },

  async asyncData({ params, route, app: { $rentcarsApi, $routeResolver, i18n }, redirect, error }) {
    let responsePage = {}
    let seoTags = {}

    if (route.name === 'routes.locations.country.pt-br' && route.params.countrySlug === 'portugal') {
      return redirect(302, $routeResolver('routes.locations.countryV2PT', i18n.locale, true))
    } else if (route.name === 'routes.locations.country.es-co' && route.params.countrySlug === 'mexico') {
      return redirect(302, $routeResolver('routes.locations.countryV2MX', i18n.locale, true))
    }

    await $rentcarsApi
      .get('v3/places/country', {
        countrySlug: params.countrySlug,
        type: 'Cidade'
      })
      .then((response) => {
        responsePage = response.data
      })
      .catch((e) => {
        error({
          statusCode: 404,
          message: 'Not Found.'
        })
      })

    if ('country' in responsePage) {
      const seoSettings = new SeoService($rentcarsApi)
      seoTags = await seoSettings.seoPath({ path: route.path })
    }

    return {
      ...responsePage,
      seoTags
    }
  },

  mounted() {
    if (this.hotJarRoutes[this.$route.path]) {
      this.gtmDataLayer({
        event: 'hotJarEntryPages'
      })
    }
  },

  head() {
    return {
      title: this.getPageTitle(),
      link: this.getPageLinks(),
      meta: this.getPageMeta(),
      script: [
        {
          innerHTML: JSON.stringify(this.createBreadcrumbListScript(this.jsonld.BreadcrumbList, true)),
          type: 'application/ld+json'
        }
      ],
      __dangerouslyDisableSanitizers: ['script']
    }
  },

  computed: {
    jsonld() {
      return {
        BreadcrumbList: [{
          position: 1,
          name: this.$t('common.rentcars'),
          item: this.getRoute('routes.home')
        }, {
          position: 2,
          name: this.$t('common.cities'),
          item: this.getRoute('routes.locations')
        }, {
          position: 3,
          name: this.country.PaisI18n[this.$i18n.locale],
          item: this.getRoute('routes.locations.country', {
            countrySlug: this.unavailableVehicle ? this.country.PaisI18n.ReferenciaI18n[this.$i18n.locale] : this.country.ReferenciaI18n[this.$i18n.locale]
          })
        }]
      }
    },

    imageHeader() {
      if (typeof this.country === 'undefined') {
        return this.$mobileCdn('/images/locations/featured-locations/headline/default-mobile-location-banner.png')
      }
      return this.$mobileCdn('/images/locations/featured-locations/headline/' + this.country.Codigo + '-mobile-country-banner.png')
    },

    imageHeaderDefault() {
      return this.$mobileCdn('/images/locations/featured-locations/headline/default-mobile-location-banner.png')
    },

    isCheckedUrlToken() {
      return (process.env.TOKEN_LP === this.$route.query.token)
    },

    currentDate() {
      return (this.$route.query.data && this.isCheckedUrlToken) ? this.$route.query.data : new Moment().format('YYYY-MM-DD')
    }
  },

  methods: {
    getRoute(routeName, params = {}) {
      return this.$routeResolver(routeName, this.$i18n.locale, process.env.BASE_URL, params)
    },

    headerTitle() {
      const headerTitle = this.seoTags && this.seoTags.h1 ? this.seoTags.h1 : this.$t('seo.locations.country.h1', {
        COUNTRY_WITH_PREPOSITION: this.country.NomeComPreposicao
      })
      return headerTitle
    },

    getPageTitle() {
      const pageTitle = this.seoTags && this.seoTags.title ? this.seoTags.title : this.$t('seo.locations.country.title', {
        COUNTRY_WITH_PREPOSITION: this.country.NomeComPreposicao,
        COUNTRY: this.country.Nome
      })
      return pageTitle
    },

    getPageDescription() {
      const description = this.seoTags && this.seoTags.description ? this.seoTags.description : this.$t('seo.locations.country.description', {
        COUNTRY_WITH_PREPOSITION: this.country.NomeComPreposicao
      })
      return description
    },

    getPageMeta() {
      const metas = []

      metas.push({
        hid: 'title',
        name: 'title',
        content: this.getPageTitle()
      })

      metas.push({
        hid: 'description',
        name: 'description',
        content: this.getPageDescription()
      })

      metas.push({
        hid: 'og:title',
        name: 'og:title',
        content: this.getPageTitle()
      })

      metas.push({
        hid: 'og:description',
        name: 'og:description',
        content: this.getPageDescription()
      })

      return metas
    },

    getPageLinks() {
      const links = i18nConfig.availableLocales.map((key) => {
        return {
          rel: 'alternate',
          hid: 'lang-' + key,
          href: this.getPageAlternateForLocale(key),
          hreflang: key
        }
      })

      links.push({
        rel: 'alternate',
        hid: 'lang-xlang',
        href: this.getPageAlternateForLocale('en'),
        hreflang: 'x-default'
      })

      links.push({
        hid: 'canonical',
        rel: 'canonical',
        href: process.env.CANONICAL_URL + this.$route.path
      })

      return links
    },

    getPageAlternateForLocale(key) {
      return this.$routeResolver('routes.locations.country', key, process.env.BASE_URL, {
        countrySlug: this.unavailableVehicle ? this.country.PaisI18n.ReferenciaI18n[key] : this.country.ReferenciaI18n[key]
      })
    },

    notFoundMessage() {
      return this.$t('static-pages.unavailable-location-message', {
        location: this.country.NomeComPreposicao,
        linkToLocations: this.$routeResolver('routes.locations')
      })
    },

    canShowBannerFeriasPortugal() {
      return Moment(this.currentDate).isBetween(PROMOTION_DATES.feriasPortugalAgosto.starts, PROMOTION_DATES.feriasPortugalAgosto.ends, null, '[]') &&
            ((this.$store.getters.getLocale === 'pt-pt' && this.$store.getters.getAppResidenceCountry === 'PT'))
    }
  }
}
